import axios, { AxiosInstance } from 'axios';
import { ErrorObject } from "~/utils/types.ts";

export const getErrorMessage = (err: unknown, defaultMessage = 'Please try again.'): string => {
    if (!err || typeof err !== 'object') {
        return defaultMessage;
    }

    const errorObj = err as ErrorObject;
    const [firstKey] = Object.keys(errorObj);

    return firstKey && Array.isArray(errorObj[firstKey]) && errorObj[firstKey].length > 0
        ? firstKey + ' ' + errorObj[firstKey][0]
        : defaultMessage;
};

const BASE_API_URL = import.meta.env.VITE_APP_BASE_API_URL;

// List of endpoints that don't need auth or refresh
const public_allowed = [
    '/login', '/register', '/refresh', '/install', '/callback', '/shopify/install/'
];

// List of Shopify-specific endpoints that need credentials
const shopify_endpoints = [
    '/shopify/install/',
    '/shopify/callback/'
];


// Create two separate axios instances
const API: AxiosInstance = axios.create({
    baseURL: BASE_API_URL,
    timeout: 120000,
});

// Separate instance for Shopify auth with credentials
const ShopifyAPI: AxiosInstance = axios.create({
    baseURL: BASE_API_URL,
    timeout: 120000,
    withCredentials: true,
});

// Regular API interceptor
API.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');

        if (token && !public_allowed.some(url => config.url?.includes(url))) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Shopify API interceptor
ShopifyAPI.interceptors.request.use(
    (config) => {
        const session = localStorage.getItem('shopifySession') ? JSON.parse(localStorage.getItem('shopifySession')!) : null;

        if (session && config.url) {
            // Add shop and hmac to every request
            const params = new URLSearchParams(config.url.split('?')[1] || '');
            params.append('shop', session.shop);
            params.append('hmac', session.hmac);
            params.append('sec_public_id', session.public_id);

            const baseUrl = config.url.split('?')[0];
            config.url = `${baseUrl}?${params.toString()}`;
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Utility to clear tokens and redirect to login with expired message
const deleteTokens = () => {
    const shopifySession = localStorage.getItem('shopifySession');
    let app_public_key = null;
    if (shopifySession) {
        app_public_key = JSON.parse(shopifySession)?.public_id;
    }
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    if(app_public_key) {
        localStorage.removeItem('shopifySession');
        window.location.href = `/app/${app_public_key}/login`;
    } else {
        window.location.href = '/login?at=expired';
    }

}

// Regular API response interceptor
API.interceptors.response.use(
    (response) => response,
    async (error) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
            console.log(`Error ${error.response.status}: Unauthorized`);
            deleteTokens();
        }
        return Promise.reject(error);
    }
);

// Shopify API response interceptor
ShopifyAPI.interceptors.response.use(
    (response) => response,
    async (error) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
            console.log(`Error ${error.response.status}: Shopify authentication expired`);

        }
        return Promise.reject(error);
    }
);

// Helper function to determine which API instance to use
export const getAPI = (endpoint: string): AxiosInstance => {
    if (shopify_endpoints.some(url => endpoint.includes(url))) {
        return ShopifyAPI;
    }
    return API;
};

export { API as default, ShopifyAPI };